import { useEffect } from 'react';

type HotkeyHandler = (e: KeyboardEvent) => void;

export type Hotkey = {
  /** Lowercase key, e.g. 'f', 'n', 's', '?'. */
  key: string;
  /** Requires Ctrl/Cmd held. */
  ctrl?: boolean;
  /** Requires Shift held. */
  shift?: boolean;
  /** Ignore when focus is in an input/textarea/select/contenteditable. */
  allowInInput?: boolean;
  handler: HotkeyHandler;
  description: string;
};

const isEditableTarget = (el: EventTarget | null): boolean => {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
};

/**
 * Registers global keyboard shortcuts. Pass an array of hotkeys; each is
 * matched on keydown. Ctrl/Cmd shortcuts are matched case-insensitively.
 */
export function useHotkeys(hotkeys: Hotkey[]): void {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const meta = e.ctrlKey || e.metaKey;
      for (const hk of hotkeys) {
        const keyMatches = e.key.toLowerCase() === hk.key.toLowerCase();
        const ctrlMatches = Boolean(hk.ctrl) === meta;
        const shiftMatches = Boolean(hk.shift) === e.shiftKey;
        if (!keyMatches || !ctrlMatches || !shiftMatches) continue;
        if (!hk.allowInInput && isEditableTarget(e.target)) continue;
        e.preventDefault();
        hk.handler(e);
        return;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [hotkeys]);
}
